import { LogOut, User } from "lucide-react";
import { Link } from "react-router-dom";

import { logout } from "@/core/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/core/components/ui/avatar";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";

export type ProfileUser = {
  fullName: string;
  email: string;
  initials: string;
  imageUrl?: string;
};

function toInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return initials || "?";
}

/** Utilisateur courant lu depuis les claims du token Keycloak (session store). */
function useSessionUser(): ProfileUser {
  const userInfo = useSessionStore((s) => s.userInfo);
  const claims = (userInfo ?? {}) as Record<string, unknown>;
  const fullName =
    (claims.name as string | undefined) ??
    (claims.preferred_username as string | undefined) ??
    "";

  return {
    fullName,
    email: (claims.email as string | undefined) ?? "",
    initials: toInitials(fullName),
  };
}

export function ProfileDropdown() {
  const { t } = useTypedTranslation();
  const user = useSessionUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full hover:bg-transparent"
          aria-label={t("layout.profile.myAccount")}
        >
          <Avatar>
            {user.imageUrl && (
              <AvatarImage src={user.imageUrl} alt={user.fullName} />
            )}
            <AvatarFallback>{user.initials}</AvatarFallback>
          </Avatar>
          <span className="absolute bottom-0 right-0 block size-2 rounded-full bg-success ring-2 ring-card" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-4 px-2 py-2.5 font-normal">
            <div className="relative">
              <Avatar className="size-10">
                {user.imageUrl && (
                  <AvatarImage src={user.imageUrl} alt={user.fullName} />
                )}
                <AvatarFallback>{user.initials}</AvatarFallback>
              </Avatar>
              <span className="absolute bottom-0 right-0 block size-2 rounded-full bg-success ring-2 ring-card" />
            </div>
            <div className="flex flex-1 flex-col items-start">
              <NoTranslate
                as="span"
                className="text-base font-semibold text-foreground"
              >
                {user.fullName}
              </NoTranslate>
              <NoTranslate
                as="span"
                className="text-sm text-muted-foreground"
              >
                {user.email}
              </NoTranslate>
            </div>
          </DropdownMenuLabel>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/app/settings/profile">
              <User />
              <span>{t("layout.profile.myAccount")}</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            onSelect={() => logout()}
          >
            <LogOut />
            <span>{t("layout.profile.signOut")}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
